import { useQueryClient } from "@tanstack/react-query"
import { FileDown, FileSpreadsheet } from "lucide-react"
import { type RefObject, useRef, useState } from "react"

import { api, enviarParaBinario } from "@/api/cliente"
import { comoSeLlama, type Empresa } from "@/componentes/empresa/tipos"
import { LogoSetieca } from "@/componentes/marca/LogoSetieca"
import { logoComoPng } from "@/componentes/reportes/pdfReporte"
import { MenuAcciones } from "@/componentes/ui/MenuAcciones"
import { usarSesion } from "@/lib/sesion"

import { documentoDe } from "./desdeTabla"
import { type DocumentoExportable, nombreDeArchivo } from "./modelo"
import { type Membrete, pdfExportable } from "./pdf"
import { conTotales, indicadoresDe } from "./totales"

/**
 * «Exportar» → Excel o PDF, para cualquier pantalla (29/10).
 *
 * Lo normal es pasarle `contenido`: la caja que envuelve las tablas de la vista, y se
 * exporta lo que haya pintado ahí (ver `desdeTabla.ts`). Una vista que no es tabla
 * —tarjetas, un resumen— arma su propio `documento`.
 *
 * ⚠ **`subtitulo` dice QUÉ se está viendo** —el período, los filtros, la búsqueda—. Un
 * Excel de «Movimientos» sin decir de qué cuenta y de qué mes es una lista de cifras
 * que nadie puede volver a encontrar.
 *
 * ⚠ **La marca es la VECTORIAL, rasterizada aquí**, la misma de los reportes: el logo
 * subido en «Empresa» está vacío en producción (ver `MembreteImpreso`).
 */

const VINO = "rgb(122, 27, 56)" // --impreso-vino

function bajar(blob: Blob, nombre: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = nombre
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

function aBase64(bytes: Uint8Array): string {
  let binario = ""
  for (const b of bytes) binario += String.fromCharCode(b)
  return btoa(binario)
}

export function membreteDe(e: Empresa): Membrete {
  const telefono = [e.pais_telefono, e.telefono].filter(Boolean).join(" ")
  const contacto = [telefono && `Tel. ${telefono}`, e.email, e.sitio_web]
    .filter(Boolean)
    .join("  ·  ")
  return { nombre: comoSeLlama(e), rif: e.rif, direccion: e.direccion, contacto: contacto || null }
}

export function Exportar({
  titulo,
  subtitulo,
  contenido,
  documento,
  etiqueta = "Exportar",
}: {
  titulo: string
  subtitulo?: string
  /** La caja con las tablas que se exportan. */
  contenido?: RefObject<HTMLElement | null>
  /** Para lo que no es tabla: el documento ya armado. Manda sobre `contenido`. */
  documento?: () => DocumentoExportable
  etiqueta?: string
}) {
  const clienteQuery = useQueryClient()
  const autor = usarSesion((e) => e.usuario?.nombre)
  const marca = useRef<HTMLSpanElement | null>(null)
  const [ocupado, setOcupado] = useState<"pdf" | "excel" | null>(null)
  const [fallo, setFallo] = useState<string | null>(null)

  const armar = (): DocumentoExportable | null => {
    const base = documento
      ? documento()
      : contenido?.current
        ? documentoDe(contenido.current, titulo, subtitulo)
        : null
    if (base === null) return null
    // ⚠ Las tarjetas de indicadores se buscan en TODA la pantalla, no sólo en
    // `contenido`: casi siempre van encima, fuera de la caja de la tabla.
    const zona = marca.current?.closest("main") ?? contenido?.current ?? null
    return {
      ...base,
      indicadores: base.indicadores ?? (zona ? indicadoresDe(zona) : []),
      secciones: base.secciones.map(conTotales),
    }
  }

  async function exportar(formato: "pdf" | "excel") {
    setFallo(null)
    const doc = armar()
    if (doc === null) {
      setFallo("No hay nada que exportar todavía.")
      return
    }
    setOcupado(formato)
    try {
      const logo = await logoComoPng(marca.current?.querySelector("svg") ?? null, VINO)
      if (formato === "pdf") {
        const empresa = await clienteQuery.fetchQuery({
          queryKey: ["empresa"],
          queryFn: () => api.get<Empresa>("/api/v1/empresa"),
        })
        const bytes = await pdfExportable(doc, membreteDe(empresa), { logo, autor })
        bajar(new Blob([bytes], { type: "application/pdf" }), nombreDeArchivo(doc.titulo, "pdf"))
      } else {
        const blob = await enviarParaBinario("/api/v1/exportar/excel", {
          ...doc,
          logo_png: logo ? aBase64(logo) : null,
        })
        bajar(blob, nombreDeArchivo(doc.titulo, "xlsx"))
      }
    } catch (e) {
      setFallo(e instanceof Error ? e.message : "No se pudo exportar")
    } finally {
      setOcupado(null)
    }
  }

  return (
    <span className="no-print inline-flex flex-col items-end gap-1" data-exportar="no">
      <span ref={marca} hidden={true}>
        <LogoSetieca />
      </span>
      <MenuAcciones
        etiqueta={ocupado ? "Generando…" : etiqueta}
        opciones={[
          {
            id: "excel",
            etiqueta: "Excel (.xlsx)",
            ayuda: "Las cifras como números, listas para sumar y filtrar",
            icono: <FileSpreadsheet size={16} aria-hidden={true} />,
            onSelect: () => exportar("excel"),
            deshabilitada: ocupado !== null,
          },
          {
            id: "pdf",
            etiqueta: "PDF",
            ayuda: "Con el membrete de la agencia, para enviar o imprimir",
            icono: <FileDown size={16} aria-hidden={true} />,
            onSelect: () => exportar("pdf"),
            deshabilitada: ocupado !== null,
          },
        ]}
      />
      {fallo && (
        <span role="alert" className="text-xs" style={{ color: "var(--destructive)" }}>
          {fallo}
        </span>
      )}
    </span>
  )
}
